"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ResolveAcceptLanguage = exports.MATCH_TYPES = void 0;
var locale_1 = require("./locale");
var lookup_list_1 = require("./lookup-list");
/** Match type enumeration. */
exports.MATCH_TYPES = {
    localeBased: 'localeBased',
    languageBased: 'languageBased',
    relatedLocaleBased: 'relatedLocaleBased',
    defaultLocale: 'defaultLocale',
};
/** Resolve the preferred locale from an HTTP `Accept-Language` header. */
var ResolveAcceptLanguage = /** @class */ (function () {
    /**
     * Create a new `ResolveAcceptLanguage` object.
     *
     * All locale identifiers provided as parameters must following the BCP 47 `language`-`country` (case insensitive).
     *
     * @param acceptLanguageHeader - The value of an HTTP request `Accept-Language` header (also known as a "language priority list").
     * @param locales - An array of locale identifiers. The order will be used for matching where the first identifier will be more
     * likely to be matched than the last identifier.
     */
    function ResolveAcceptLanguage(acceptLanguageHeader, locales, defaultLocale) {
        // Check if the locales are valid.
        locales.forEach(function (locale) {
            if (!locale_1.default.isLocale(locale, false)) {
                throw new Error("invalid locale identifier '".concat(locale, "'"));
            }
        });
        // Check if the default locale is valid.
        if (!locale_1.default.isLocale(defaultLocale, false)) {
            throw new Error("invalid default locale identifier '".concat(defaultLocale, "'"));
        }
        // Check if the default locale is included in the locales.
        if (!locales.some(function (locale) { return locale.toLowerCase() === defaultLocale.toLowerCase(); })) {
            throw new Error('the default locale must be included in the locales');
        }
        this.defaultLocale = new locale_1.default(defaultLocale).identifier;
        var lookupList = new lookup_list_1.default(acceptLanguageHeader, locales, defaultLocale);
        // Check if the match if locale based.
        this.localeBasedMatch = lookupList.getLocaleBasedMatch();
        if (this.localeBasedMatch) {
            return;
        }
        // Check if the match is language based.
        this.languageBasedMatch = lookupList.getLanguageBasedMatch();
        if (this.languageBasedMatch) {
            return;
        }
        // Check if the match is related-locale based.
        this.relatedLocaleBasedMatch = lookupList.getRelatedLocaleBasedMatch();
        if (this.relatedLocaleBasedMatch) {
            return;
        }
    }
    /**
     * Get the type of match.
     *
     * @returns The type of match.
     */
    ResolveAcceptLanguage.prototype.getMatchType = function () {
        return this.localeBasedMatch
            ? exports.MATCH_TYPES.localeBased
            : this.languageBasedMatch
                ? exports.MATCH_TYPES.languageBased
                : this.relatedLocaleBasedMatch
                    ? exports.MATCH_TYPES.relatedLocaleBased
                    : exports.MATCH_TYPES.defaultLocale;
    };
    /**
     * Get the matching locale.
     *
     * @returns The matching locale.
     */
    ResolveAcceptLanguage.prototype.getMatch = function () {
        var _a, _b, _c;
        return ((_c = (_b = (_a = this.localeBasedMatch) !== null && _a !== void 0 ? _a : this.languageBasedMatch) !== null && _b !== void 0 ? _b : this.relatedLocaleBasedMatch) !== null && _c !== void 0 ? _c : this.defaultLocale);
    };
    return ResolveAcceptLanguage;
}());
exports.ResolveAcceptLanguage = ResolveAcceptLanguage;
/**
 * Resolve the preferred locale from an HTTP `Accept-Language` header.
 *
 * All locale identifiers provided as parameters must following the BCP 47 `language`-`country` (case insensitive).
 *
 * @param acceptLanguageHeader - The value of an HTTP request `Accept-Language` header (also known as a "language priority list").
 * @param locales - An array of locale identifiers that must include the default locale. The order will be used for matching where
 * the first identifier will be more likely to be matched than the last identifier.
 * @param defaultLocale - The default locale identifier when no match is found.
 *
 * @returns The locale identifier which was the best match, in case-normalized format.
 *
 * @example
 * // returns 'fr-CA'
 * resolveAcceptLanguage(
 *   'fr-CA;q=0.01,en-CA;q=0.1,en-US;q=0.001',
 *   ['en-US', 'fr-CA'],
 *   'en-US'
 * )
 */
var resolveAcceptLanguage = function (acceptLanguageHeader, locales, defaultLocale) {
    return new ResolveAcceptLanguage(acceptLanguageHeader, locales, defaultLocale).getMatch();
};
exports.default = resolveAcceptLanguage;
